import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Eye, Users, MousePointer, TrendingUp, BarChart3,
  ArrowUpRight, ArrowDownRight, Globe, Zap, Phone, Calendar,
  MessageSquare, MapPin, ExternalLink, Gift, Filter, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const DATE_RANGES = [
  { label: "7 days", days: 7 },
  { label: "14 days", days: 14 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

const EVENT_LABELS = {
  listing_view: { label: "Listing Views", icon: Eye, color: "#7C3AED" },
  call_click: { label: "Call Clicks", icon: Phone, color: "#10B981" },
  booking_click: { label: "Booking Clicks", icon: Calendar, color: "#F59E0B" },
  form_submit: { label: "Form Submissions", icon: MessageSquare, color: "#3B82F6" },
  chat_click: { label: "Chat Starts", icon: MessageSquare, color: "#06B6D4" },
  website_click: { label: "Website Clicks", icon: ExternalLink, color: "#8B5CF6" },
  directions_click: { label: "Directions", icon: MapPin, color: "#EF4444" },
  offer_click: { label: "Offer Clicks", icon: Gift, color: "#F97316" },
};

const TIER_COLORS = {
  hot: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/20", label: "Hot Lead" },
  warm: { bg: "bg-[#F59E0B]/10", text: "text-[#F59E0B]", border: "border-[#F59E0B]/20", label: "Warm Lead" },
  engaged: { bg: "bg-[#3B82F6]/10", text: "text-[#3B82F6]", border: "border-[#3B82F6]/20", label: "Engaged" },
  visitor: { bg: "bg-white/5", text: "text-[#94A3B8]", border: "border-white/5", label: "Visitor" },
};

export default function AnalyticsDashboard() {
  const { getAuthHeader } = useAuth();
  const [rangeDays, setRangeDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [events, setEvents] = useState([]);
  const [sources, setSources] = useState({ sources: [], mediums: [], campaigns: [] });
  const [timeline, setTimeline] = useState([]);
  const [visitors, setVisitors] = useState({ visitors: [], score_distribution: {}, total_visitors: 0 });
  const [topListings, setTopListings] = useState([]);
  const [activeTab, setActiveTab] = useState("sources");

  const dateParams = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - rangeDays * 86400000);
    return `start=${start.toISOString()}&end=${end.toISOString()}`;
  }, [rangeDays]);

  useEffect(() => { fetchAll(); }, [dateParams]);

  const fetchAll = async () => {
    setLoading(true);
    const headers = getAuthHeader();
    try {
      const [ovRes, evRes, srcRes, tlRes, visRes, topRes] = await Promise.all([
        axios.get(`${API}/analytics/overview?${dateParams}`, { headers }),
        axios.get(`${API}/analytics/events?${dateParams}`, { headers }),
        axios.get(`${API}/analytics/sources?${dateParams}`, { headers }),
        axios.get(`${API}/analytics/timeline?${dateParams}`, { headers }),
        axios.get(`${API}/analytics/visitors?${dateParams}`, { headers }),
        axios.get(`${API}/analytics/top-listings?${dateParams}`, { headers }),
      ]);
      setOverview(ovRes.data);
      setEvents(evRes.data.events || []);
      setSources(srcRes.data || { sources: [], mediums: [], campaigns: [] });
      setTimeline(tlRes.data.timeline || []);
      setVisitors(visRes.data || { visitors: [], score_distribution: {}, total_visitors: 0 });
      setTopListings(topRes.data.listings || []);
    } catch (err) {
      console.error("Analytics fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const maxTimelineValue = useMemo(() => {
    return Math.max(...timeline.map(d => d.views || 0), 1);
  }, [timeline]);

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Analytics & Lead Intelligence</h1>
          <p className="text-[#94A3B8] mt-1">Track visitor behavior, lead scores, and conversion performance</p>
        </div>
        <div className="flex items-center gap-2">
          {DATE_RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setRangeDays(r.days)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${rangeDays === r.days ? "bg-[#7C3AED] text-white" : "bg-white/5 text-[#94A3B8] hover:bg-white/10"}`}
              data-testid={`range-${r.days}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Total Views" value={overview?.total_views || 0} color="#7C3AED" />
        <StatCard icon={Users} label="Unique Visitors" value={overview?.unique_visitors || 0} color="#3B82F6" />
        <StatCard icon={Zap} label="Conversions" value={overview?.total_conversions || 0} color="#10B981" />
        <StatCard icon={TrendingUp} label="Conv. Rate" value={`${overview?.conversion_rate || 0}%`} color="#F59E0B" />
      </div>

      {/* Timeline Chart */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#7C3AED]" /> Views Over Time
        </h2>
        <div className="h-48 flex items-end gap-[2px]" data-testid="timeline-chart">
          {timeline.map((day, i) => {
            const height = maxTimelineValue > 0 ? Math.max((day.views / maxTimelineValue) * 100, 2) : 2;
            const hasConversions = day.conversions > 0;
            return (
              <div key={day.date || `tl-${i}`} className="flex-1 flex flex-col items-center justify-end group relative min-w-0">
                <div
                  className={`w-full rounded-t transition-all ${hasConversions ? "bg-[#10B981]" : "bg-[#7C3AED]/60"} group-hover:bg-[#7C3AED]`}
                  style={{ height: `${height}%`, minHeight: "2px" }}
                />
                {/* Tooltip */}
                <div className="absolute bottom-full mb-2 hidden group-hover:block bg-[#1E293B] border border-white/10 rounded-lg px-3 py-2 text-xs z-20 whitespace-nowrap pointer-events-none">
                  <p className="text-white font-semibold">{new Date(day.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</p>
                  <p className="text-[#7C3AED]">{day.views} views</p>
                  {day.conversions > 0 && <p className="text-[#10B981]">{day.conversions} conversions</p>}
                  {day.engagement > 0 && <p className="text-[#F59E0B]">{day.engagement} engagement</p>}
                </div>
              </div>
            );
          })}
        </div>
        {timeline.length > 0 && (
          <div className="flex justify-between mt-2 text-[10px] text-[#475569]">
            <span>{formatDate(timeline[0]?.date)}</span>
            <span>{formatDate(timeline[timeline.length - 1]?.date)}</span>
          </div>
        )}
        <div className="flex items-center gap-4 mt-3 text-xs text-[#64748B]">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#7C3AED]/60" /> Views</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#10B981]" /> Conversions</span>
        </div>
      </motion.div>

      {/* Two-Column: Sources + Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sources / Campaigns */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-[#3B82F6]" />
            <h2 className="text-lg font-semibold text-white">Traffic</h2>
          </div>
          {/* Sub-tabs */}
          <div className="flex gap-1 mb-4 bg-white/5 rounded-lg p-1">
            {["sources", "campaigns", "mediums"].map((t) => (
              <button key={t} onClick={() => setActiveTab(t)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${activeTab === t ? "bg-[#7C3AED] text-white" : "text-[#94A3B8] hover:text-white"}`}
                data-testid={`tab-${t}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid={`list-${activeTab}`}>
            {activeTab === "sources" && sources.sources.map((s, i) => (
              <BarRow key={`src-${s.source || i}`} label={s.source} value={s.count} max={sources.sources[0]?.count || 1} sub={`${s.unique_visitors} visitors`} />
            ))}
            {activeTab === "campaigns" && sources.campaigns.map((c, i) => (
              <BarRow key={`cmp-${c.campaign || i}`} label={c.campaign} value={c.count} max={sources.campaigns[0]?.count || 1} sub={`${c.unique_visitors} visitors`} />
            ))}
            {activeTab === "mediums" && sources.mediums.map((m, i) => (
              <BarRow key={`med-${m.medium || i}`} label={m.medium} value={m.count} max={sources.mediums[0]?.count || 1} />
            ))}
            {(activeTab === "sources" && sources.sources.length === 0) ||
             (activeTab === "campaigns" && sources.campaigns.length === 0) ||
             (activeTab === "mediums" && sources.mediums.length === 0) ? (
              <p className="text-[#64748B] text-sm text-center py-8">No data yet. Share your listing to start tracking traffic sources.</p>
            ) : null}
          </div>
        </motion.div>

        {/* Event Breakdown */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-[#10B981]" />
            <h2 className="text-lg font-semibold text-white">Events</h2>
          </div>
          <div className="space-y-3" data-testid="event-breakdown">
            {events.length === 0 && <p className="text-[#64748B] text-sm text-center py-8">No events recorded yet.</p>}
            {events.map((ev, i) => {
              const meta = EVENT_LABELS[ev.event_name] || { label: ev.event_name, color: "#94A3B8" };
              const Icon = meta.icon || Zap;
              return (
                <div key={ev.event_name || `evt-${i}`} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${meta.color}15` }}>
                      <Icon className="w-4 h-4" style={{ color: meta.color }} />
                    </div>
                    <span className="text-[#E2E8F0] text-sm">{meta.label}</span>
                  </div>
                  <span className="text-white font-semibold font-mono">{ev.count.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Lead Score Distribution */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#F59E0B]" /> Lead Score Distribution
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {Object.entries(TIER_COLORS).reverse().map(([tier, style]) => (
            <div key={tier} className={`${style.bg} border ${style.border} rounded-xl p-4 text-center`} data-testid={`tier-${tier}`}>
              <p className={`text-2xl font-bold ${style.text}`}>{visitors.score_distribution?.[tier] || 0}</p>
              <p className="text-xs text-[#94A3B8] mt-1">{style.label}</p>
            </div>
          ))}
        </div>

        {/* Recent Visitors Table */}
        <h3 className="text-sm font-semibold text-[#94A3B8] mb-3 uppercase tracking-wider">Recent Visitors</h3>
        <div className="overflow-x-auto" data-testid="visitors-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="pb-2 pr-4 text-[#64748B] font-medium">Visitor</th>
                <th className="pb-2 pr-4 text-[#64748B] font-medium">Score</th>
                <th className="pb-2 pr-4 text-[#64748B] font-medium">Tier</th>
                <th className="pb-2 pr-4 text-[#64748B] font-medium">Events</th>
                <th className="pb-2 pr-4 text-[#64748B] font-medium">Sessions</th>
                <th className="pb-2 pr-4 text-[#64748B] font-medium">Source</th>
                <th className="pb-2 text-[#64748B] font-medium">Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {visitors.visitors.slice(0, 15).map((v, i) => {
                const tier = TIER_COLORS[v.lead_tier] || TIER_COLORS.visitor;
                return (
                  <tr key={v.visitor_id || `v-${i}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-2.5 pr-4">
                      <span className="text-[#E2E8F0] font-mono text-xs">{v.visitor_id?.slice(0, 8)}...</span>
                      {v.last_company && <p className="text-[#64748B] text-xs">{v.last_company}</p>}
                    </td>
                    <td className="py-2.5 pr-4">
                      <span className={`font-bold ${tier.text}`}>{v.lead_score}</span>
                    </td>
                    <td className="py-2.5 pr-4">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${tier.bg} ${tier.text} border ${tier.border}`}>
                        {tier.label}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-[#E2E8F0]">{v.total_events}</td>
                    <td className="py-2.5 pr-4 text-[#E2E8F0]">{v.session_count}</td>
                    <td className="py-2.5 pr-4 text-[#94A3B8]">{v.source}{v.medium ? ` / ${v.medium}` : ""}</td>
                    <td className="py-2.5 text-[#64748B] text-xs">{v.last_seen ? formatDateTime(v.last_seen) : "—"}</td>
                  </tr>
                );
              })}
              {visitors.visitors.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-[#64748B]">No visitor data yet. Views will populate as visitors browse your listings.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Top Listings (admin view) */}
      {topListings.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#7C3AED]" /> Top Listings
          </h2>
          <div className="overflow-x-auto" data-testid="top-listings-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="pb-2 pr-4 text-[#64748B] font-medium">Listing</th>
                  <th className="pb-2 pr-4 text-[#64748B] font-medium">Category</th>
                  <th className="pb-2 pr-4 text-[#64748B] font-medium">City</th>
                  <th className="pb-2 pr-4 text-[#64748B] font-medium">Views</th>
                  <th className="pb-2 pr-4 text-[#64748B] font-medium">Visitors</th>
                  <th className="pb-2 pr-4 text-[#64748B] font-medium">Conversions</th>
                  <th className="pb-2 text-[#64748B] font-medium">Engagement</th>
                </tr>
              </thead>
              <tbody>
                {topListings.map((l, i) => (
                  <tr key={l.listing_id || `tl-${i}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-2.5 pr-4 text-[#E2E8F0]">{l.company_name || l.listing_id?.slice(0, 12)}</td>
                    <td className="py-2.5 pr-4 text-[#94A3B8]">{l.category || "—"}</td>
                    <td className="py-2.5 pr-4 text-[#94A3B8]">{l.city || "—"}</td>
                    <td className="py-2.5 pr-4 text-white font-semibold">{l.views}</td>
                    <td className="py-2.5 pr-4 text-[#3B82F6]">{l.unique_visitors}</td>
                    <td className="py-2.5 pr-4 text-[#10B981]">{l.conversions}</td>
                    <td className="py-2.5 text-[#F59E0B]">{l.engagement}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#0F172A] border border-white/10 rounded-xl p-5"
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
      <p className="text-2xl font-bold text-white">{typeof value === "number" ? value.toLocaleString() : value}</p>
      <p className="text-xs text-[#64748B] mt-1">{label}</p>
    </motion.div>
  );
}

function BarRow({ label, value, max, sub }) {
  const pct = max > 0 ? Math.max((value / max) * 100, 3) : 3;
  return (
    <div className="group">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[#E2E8F0] text-sm truncate max-w-[200px]">{label}</span>
        <div className="flex items-center gap-2">
          {sub && <span className="text-[#64748B] text-xs">{sub}</span>}
          <span className="text-white font-mono text-sm font-semibold">{value.toLocaleString()}</span>
        </div>
      </div>
      <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full bg-[#7C3AED] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDateTime(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
